import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, Copy, Images, Phone, Play, Users } from 'lucide-react';
import './customer-c.css';

const CUSTOMER_C_BASE = '/customer/c';
const BANK_LINE = '신한은행 100-035-387966 (주)제제미미';
const BANK_ACCOUNT = '100-035-387966';

type CustomerCScreen = 'landing' | 'payment' | 'form';
type RequestType = 'gift_direct' | 'phone_consult';

const normalizePathname = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

const getPageQuery = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return new URLSearchParams(window.location.search).get('page');
  } catch {
    return null;
  }
};

const resolveScreen = (pathname: string): CustomerCScreen => {
  const page = getPageQuery();
  const route = page && page.startsWith('customer/c')
    ? `/${page}`
    : normalizePathname(pathname);

  if (route === `${CUSTOMER_C_BASE}/payment`) {
    return 'payment';
  }

  if (route === `${CUSTOMER_C_BASE}/form`) {
    return 'form';
  }

  return 'landing';
};

const navigateTo = (path: string): void => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const isLikelyPhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return /^01\d{8,9}$/.test(digits);
};

type ApplyFormProps = {
  requestType: RequestType;
  title: string;
  subtitle: string;
  submitLabel: string;
  showChildField?: boolean;
  defaultConsult?: boolean;
};

const ApplyForm: React.FC<ApplyFormProps> = ({
  requestType,
  title,
  subtitle,
  submitLabel,
  showChildField = false,
  defaultConsult = false,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [childContact, setChildContact] = useState('');
  const [wantsConsult, setWantsConsult] = useState(defaultConsult);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timer = window.setTimeout(() => {
      setToast(null);
    }, 2200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [toast]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (name.trim().length < 2) {
      setToast('성함을 2글자 이상 적어주세요');
      return;
    }

    if (showChildField && childContact.trim().length === 0) {
      setToast('자녀 휴대폰번호 또는 초대코드를 적어주세요');
      return;
    }

    if (!isLikelyPhone(phone)) {
      setToast('전화번호를 다시 확인해주세요 (예: 010-1234-5678)');
      return;
    }

    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 400);
  };

  const copyAccount = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(BANK_ACCOUNT);
      setToast('계좌번호를 복사했어요');
    } catch {
      setToast('복사에 실패했어요. 직접 입력해주세요');
    }
  };

  if (submitted) {
    return (
      <main className="customer-c-page customer-c-page--done">
        {toast ? <div className="customer-c-toast" role="status">{toast}</div> : null}
        <h1 className="customer-c-done-title">신청이 접수됐어요</h1>
        <p className="customer-c-done-lead">
          입력하신 전화번호로 연락드릴게요. 평일 오전 10시 ~ 오후 6시 사이에 전화드려요.
        </p>
        <p className="customer-c-done-note">확인 알림톡을 먼저 보내드릴게요.</p>
        {requestType === 'gift_direct' ? (
          <div className="customer-c-account">
            <p className="customer-c-account-label">입금 계좌</p>
            <p className="customer-c-account-line">{BANK_LINE}</p>
            <p className="customer-c-account-amount">입금 금액 69,000원 / 1년</p>
            <button type="button" className="customer-c-copy" onClick={() => { void copyAccount(); }}>
              <Copy aria-hidden="true" />
              {' '}
              계좌번호 복사하기
            </button>
          </div>
        ) : null}
        <div className="customer-c-dock">
          <button
            type="button"
            className="customer-c-btn customer-c-btn-outline"
            onClick={() => navigateTo(CUSTOMER_C_BASE)}
          >
            처음으로 돌아가기
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="customer-c-page">
      {toast ? <div className="customer-c-toast" role="status">{toast}</div> : null}
      <button type="button" className="customer-c-back" onClick={() => navigateTo(CUSTOMER_C_BASE)}>
        <ChevronLeft aria-hidden="true" />
        {' '}
        뒤로가기
      </button>
      <h1 className="customer-c-page-title">{title}</h1>
      <p className="customer-c-page-lead">{subtitle}</p>
      <form id="apply-form" className="customer-c-form" onSubmit={handleSubmit}>
        <div className="customer-c-field">
          <label htmlFor="name">성함</label>
          <input
            id="name"
            className="customer-c-input"
            maxLength={50}
            placeholder="예) 김영자"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="customer-c-field">
          <label htmlFor="phone">전화번호</label>
          <input
            id="phone"
            className="customer-c-input"
            type="text"
            placeholder="예) 010-1234-5678"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>
        {showChildField ? (
          <div className="customer-c-field">
            <label htmlFor="child">구독시킬 자녀 정보</label>
            <input
              id="child"
              className="customer-c-input"
              maxLength={100}
              placeholder="자녀 휴대폰번호 또는 앱 초대코드"
              value={childContact}
              onChange={(event) => setChildContact(event.target.value)}
            />
          </div>
        ) : null}
        <div className="customer-c-check">
          <label className="customer-c-check-row">
            <button
              type="button"
              role="checkbox"
              aria-checked={wantsConsult}
              className={`customer-c-checkbox${wantsConsult ? ' is-checked' : ''}`}
              onClick={() => setWantsConsult((current) => !current)}
            >
              {wantsConsult ? <Check aria-hidden="true" /> : null}
            </button>
            <span className="customer-c-check-label">
              <Phone aria-hidden="true" />
              전화 상담을 받고 싶어요
            </span>
          </label>
          <p className="customer-c-check-desc">
            평일 오전 10시 ~ 오후 6시 사이 상담원이 전화드려요
          </p>
        </div>
      </form>
      <div className="customer-c-dock">
        <button
          type="submit"
          form="apply-form"
          className="customer-c-btn customer-c-btn-primary"
          disabled={submitting}
        >
          {submitting ? '신청 중이에요...' : submitLabel}
        </button>
      </div>
    </main>
  );
};

const GiftLanding: React.FC = () => (
  <>
    <main className="customer-c-main">
      <h1 className="customer-c-title">{'손주 영상,\n끊기지 말고\n길게 보고 싶으시죠?'}</h1>
      <p className="customer-c-lead">
        {'자녀 계정을 구독하면 온 가족이 광고 없이,\n최대 10분 영상도 편하게 볼 수 있어요'}
      </p>
      <section className="customer-c-benefits">
        <h2>특별한 구독 혜택들</h2>
        <div className="customer-c-benefit-grid">
          <article className="customer-c-benefit">
            <span className="customer-c-benefit-icon">
              <Users aria-hidden="true" />
            </span>
            <h3>{'온 가족이\n광고 없이 편하게'}</h3>
          </article>
          <article className="customer-c-benefit">
            <span className="customer-c-benefit-icon">
              <Play aria-hidden="true" fill="currentColor" />
            </span>
            <h3>{'손주 영상\n끊김 없이 길게'}</h3>
          </article>
          <article className="customer-c-benefit">
            <span className="customer-c-benefit-icon">
              <Images aria-hidden="true" />
            </span>
            <h3>{'밀린 사진 한 번에\n우르르 업로드'}</h3>
          </article>
        </div>
      </section>
      <section className="customer-c-price">
        <span className="customer-c-price-badge">지금 구독시켜드리면 드리는 가격</span>
        <p className="customer-c-price-hero">1인당 월 719원꼴</p>
        <p className="customer-c-price-sub">가족 8명 기준</p>
        <div className="customer-c-price-divider" />
        <p className="customer-c-price-year">
          1년
          {' '}
          <s>99,000원</s>
          {' '}
          → 69,000원 / 1년
        </p>
      </section>
      <section className="customer-c-notice">
        <p className="customer-c-notice-title">신청하시면 확인 알림톡을 먼저 보내드려요</p>
        <p className="customer-c-notice-desc">
          전화 상담을 고르시면 평일 오전 10시 ~ 오후 6시 사이 쑥쑥찰칵 상담원이 전화드려요
        </p>
      </section>
    </main>
    <div className="customer-c-dock customer-c-dock--stack">
      <button
        type="button"
        className="customer-c-btn customer-c-btn-primary"
        onClick={() => navigateTo(`${CUSTOMER_C_BASE}/payment`)}
      >
        지금 자녀 계정 구독시켜주기
      </button>
      <button
        type="button"
        className="customer-c-btn customer-c-btn-outline"
        onClick={() => navigateTo(`${CUSTOMER_C_BASE}/form`)}
      >
        손주 영상 오래 보는 법 상담받기
      </button>
    </div>
  </>
);

export const CustomerCView: React.FC = () => {
  const [pathname, setPathname] = useState(() => (
    typeof window === 'undefined' ? CUSTOMER_C_BASE : window.location.pathname
  ));
  const screen = useMemo(() => resolveScreen(pathname), [pathname]);

  useEffect(() => {
    const titles: Record<CustomerCScreen, string> = {
      landing: '쑥쑥찰칵 구독권 선물 — 연간 30% 할인',
      payment: '구독권 선물하기 — 1년 69,000원',
      form: '전화 상담 신청 — 쑥쑥찰칵 구독권 선물',
    };
    document.title = titles[screen];
  }, [screen]);

  useEffect(() => {
    const syncPathname = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener('popstate', syncPathname);
    return () => {
      window.removeEventListener('popstate', syncPathname);
    };
  }, []);

  return (
    <div className="customer-c">
      {screen === 'payment' ? (
        <ApplyForm
          requestType="gift_direct"
          title="자녀 계정을 구독시켜요"
          subtitle="1년 69,000원 (월 5,900원). 신청 후 입금 계좌를 안내드려요"
          submitLabel="자녀 계정 구독 신청하기"
          showChildField
        />
      ) : null}
      {screen === 'form' ? (
        <ApplyForm
          requestType="phone_consult"
          title="전화로 편하게 물어보세요"
          subtitle="성함과 전화번호만 남겨주시면 상담원이 전화로 알려드려요"
          submitLabel="전화 상담 신청하기"
          defaultConsult
        />
      ) : null}
      {screen === 'landing' ? <GiftLanding /> : null}
    </div>
  );
};
